'use client'

import {
   Anchor,
   Box,
   Container,
   Divider,
   Group,
   SimpleGrid,
   Stack,
   Text
} from '@blocks'
import {sitePrimaryDeepHex} from '@constants'
import {
   IconBrandFacebook,
   IconBrandInstagram,
   IconMapPin,
   IconPhone
} from '@icons'
import Image from 'next/image'
import Link from 'next/link'
import {LogoHorizontal} from '@/blocks/LogoHorizontal'

const quickLinks = [
   {label: 'Home', href: '/'},
   {label: 'Car Wash', href: '/car-wash'},
   {label: 'Dog Wash', href: '/dog-wash'},
   {label: 'Fleet', href: '/fleet'},
   {label: 'Detailing & Grooming', href: '/detailing'},
   {label: 'About', href: '/about'},
   {label: 'Contact', href: '/contact'}
]

const Kodra = ({width, height}: {width: number; height: number}) => {
   return (
      <Anchor
         href={'https://kodra.co.nz'}
         target={'_blank'}
         rel={'noopener noreferrer'}
      >
         <Image
            alt={'Kodra Logo'}
            src={'/kodra.svg'}
            width={width}
            height={height}
            style={{cursor: 'pointer'}}
         />
      </Anchor>
   )
}

export const Footer = () => {
   return (
      <Box bg={sitePrimaryDeepHex}>
         <Container
            py={88}
            size={'xl'}
         >
            <SimpleGrid
               cols={{base: 1, sm: 2, md: 3}}
               spacing={60}
            >
               <Stack gap={24}>
                  <LogoHorizontal
                     width={200}
                     height={40}
                  />
                  <Text
                     c={'rgba(255,255,255,0.92)'}
                     fz={18}
                     fw={500}
                     lh={1.7}
                  >
                     Your local car wash in Morrinsville. Clean
                     vehicles, happy customers — all in one place.
                  </Text>
                  <Group gap={10}>
                     <Anchor
                        aria-label={
                           'Clean Park Morrinsville on Facebook'
                        }
                        href={
                           'https://www.facebook.com/Cleanparkmorrinsville/'
                        }
                        target={'_blank'}
                        rel={'noopener noreferrer'}
                        style={{
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           width: 38,
                           height: 38,
                           borderRadius: 8,
                           background: 'rgba(255,255,255,0.12)',
                           color: 'white'
                        }}
                     >
                        <IconBrandFacebook size={20} />
                     </Anchor>
                     <Anchor
                        aria-label={
                           'Clean Park Morrinsville on Instagram'
                        }
                        href={
                           'https://www.instagram.com/cleanparkmorrinsville?igsh=MWh5MnBpMjlxeWp3NA%3D%3D&utm_source=qr'
                        }
                        target={'_blank'}
                        rel={'noopener noreferrer'}
                        style={{
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           width: 38,
                           height: 38,
                           borderRadius: 8,
                           background: 'rgba(255,255,255,0.12)',
                           color: 'white'
                        }}
                     >
                        <IconBrandInstagram size={20} />
                     </Anchor>
                  </Group>
               </Stack>

               <Stack gap={18}>
                  <Text
                     c={'white'}
                     fw={700}
                     fz={18}
                     tt={'uppercase'}
                     lts={1.5}
                  >
                     Quick Links
                  </Text>
                  <Stack gap={10}>
                     {quickLinks.map((link) => (
                        <Anchor
                           key={link.label}
                           c={'rgba(255,255,255,0.92)'}
                           component={Link}
                           fz={18}
                           fw={500}
                           href={link.href}
                           prefetch={false}
                           underline={'never'}
                        >
                           {link.label}
                        </Anchor>
                     ))}
                  </Stack>
               </Stack>

               <Stack gap={18}>
                  <Text
                     c={'white'}
                     fw={700}
                     fz={18}
                     tt={'uppercase'}
                     lts={1.5}
                  >
                     Find Us
                  </Text>
                  <Group
                     align={'flex-start'}
                     gap={10}
                     wrap={'nowrap'}
                  >
                     <IconMapPin
                        size={18}
                        color={'rgba(255,255,255,0.88)'}
                        style={{marginTop: 2, flexShrink: 0}}
                     />
                     <Text
                        c={'rgba(255,255,255,0.92)'}
                        fz={18}
                        fw={500}
                        lh={1.6}
                     >
                        42 Studholme Street
                        <br />
                        Morrinsville 3300
                     </Text>
                  </Group>
                  <Anchor
                     href={'tel:078896823'}
                     style={{textDecoration: 'none'}}
                  >
                     <Group
                        gap={10}
                        wrap={'nowrap'}
                     >
                        <IconPhone
                           size={18}
                           color={'rgba(255,255,255,0.88)'}
                           style={{flexShrink: 0}}
                        />
                        <Text
                           c={'rgba(255,255,255,0.92)'}
                           fz={18}
                           fw={500}
                        >
                           07 889 6823
                        </Text>
                     </Group>
                  </Anchor>
                  <Box
                     style={{
                        borderRadius: 10,
                        overflow: 'hidden',
                        border: '1px solid rgba(255,255,255,0.15)'
                     }}
                  >
                     <iframe
                        title={'Clean Park Location'}
                        src={
                           'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3158.5909141900233!2d175.52929079999998!3d-37.6588206!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6d6d094869416f51%3A0x8ebdb2b73704d04e!2s42%20Studholme%20Street%2C%20Morrinsville%203300!5e0!3m2!1sen!2snz!4v1774429125283!5m2!1sen!2snz'
                        }
                        width={'100%'}
                        height={'220'}
                        style={{border: 0, display: 'block'}}
                        allowFullScreen={false}
                        loading={'lazy'}
                        referrerPolicy={'no-referrer-when-downgrade'}
                     />
                  </Box>
               </Stack>
            </SimpleGrid>

            <Divider
               mt={50}
               mb={25}
               color={'rgba(255,255,255,0.12)'}
            />

            <Group
               gap={20}
               align={'center'}
               justify={'space-between'}
            >
               <Text
                  c={'rgba(255,255,255,0.78)'}
                  fz={18}
                  ta={'center'}
                  fw={500}
               >
                  © 2026 Clean Park Morrinsville. All rights reserved.
               </Text>
               <Kodra
                  width={200}
                  height={50}
               />
            </Group>
         </Container>
      </Box>
   )
}
